import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useRouter } from "@tanstack/react-router";

import { supabase } from "@/integrations/supabase/client";
import { getCurrentAdminStatus } from "@/lib/auth-status.functions";
import { sendWelcomeEmail } from "@/lib/welcome-email.functions";

type AuthContextValue = {
  session: Session | null;
  user: User | null;
  isAdmin: boolean;
  loading: boolean;
  adminChecked: boolean;
  refreshAdminStatus: () => void;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue>({
  session: null,
  user: null,
  isAdmin: false,
  loading: true,
  adminChecked: false,
  refreshAdminStatus: () => {},
  signOut: async () => {},
});

const ADMIN_CACHE_KEY = "wf:isAdmin";
const ADMIN_STATUS_RETRIES = 2;
const ADMIN_STATUS_RETRY_DELAY_MS = 350;
const AUTH_RESTORE_TIMEOUT_MS = 5000;

async function getClientAdminStatusFallback() {
  const [adminRpc, superAdminRpc] = await Promise.allSettled([
    supabase.rpc("current_user_is_admin"),
    supabase.rpc("current_user_is_super_admin"),
  ]);

  const adminValue =
    adminRpc.status === "fulfilled" && !adminRpc.value.error
      ? adminRpc.value.data === true
      : null;
  const superAdminValue =
    superAdminRpc.status === "fulfilled" && !superAdminRpc.value.error
      ? superAdminRpc.value.data === true
      : null;

  if (adminValue === true || superAdminValue === true) return true;
  if (adminValue !== null || superAdminValue !== null) return false;

  const { data, error } = await supabase
    .from("user_roles")
    .select("role")
    .in("role", ["admin", "super_admin"])
    .limit(1);

  if (error) throw error;
  return (data?.length ?? 0) > 0;
}

function readCachedAdmin(): boolean {
  if (typeof window === "undefined") return false;
  try { return window.sessionStorage.getItem(ADMIN_CACHE_KEY) === "1"; } catch { return false; }
}
function writeCachedAdmin(v: boolean) {
  if (typeof window === "undefined") return;
  try { window.sessionStorage.setItem(ADMIN_CACHE_KEY, v ? "1" : "0"); } catch { /* ignore */ }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  
  const cachedAdmin = readCachedAdmin();
  const [isAdmin, setIsAdmin] = useState(cachedAdmin);
  // Optimistically treat the cached admin flag as "checked" so the UI renders
  // immediately on reload; we still revalidate in the background.
  const [adminChecked, setAdminChecked] = useState(cachedAdmin);
  const [adminCheckNonce, setAdminCheckNonce] = useState(0);
  const router = useRouter();
  const queryClient = useQueryClient();
  const getCurrentAdminStatusFn = useServerFn(getCurrentAdminStatus);
  const sendWelcomeEmailFn = useServerFn(sendWelcomeEmail);

  useEffect(() => {
    let cancelled = false;
    let lastUserId: string | null = null;
    let hasResolvedInitialSession = false;
    let subscription: { unsubscribe: () => void } | null = null;
    let restoreTimeout: ReturnType<typeof setTimeout> | null = null;

    const apply = (newSession: Session | null, options?: { allowPreHydrationNull?: boolean }) => {
      if (cancelled) return;
      if (!hasResolvedInitialSession && !newSession && !options?.allowPreHydrationNull) return;
      if (restoreTimeout) {
        clearTimeout(restoreTimeout);
        restoreTimeout = null;
      }

      const nextUserId = newSession?.user?.id ?? null;
      const userChanged = hasResolvedInitialSession && nextUserId !== lastUserId;

      setSession(newSession);
      setLoading(false);

      if (!newSession) {
        setIsAdmin(false);
        setAdminChecked(true);
        writeCachedAdmin(false);
      } else if (userChanged) {
        // New user signed in — clear stale admin flag and force a re-check
        // so we don't briefly render the previous user's admin verdict.
        setIsAdmin(false);
        setAdminChecked(false);
        writeCachedAdmin(false);
      }

      lastUserId = nextUserId;
      hasResolvedInitialSession = true;

      if (userChanged) {
        queryClient.removeQueries({ queryKey: ["profile"] });
        setTimeout(() => {
          router.invalidate();
          queryClient.invalidateQueries();
        }, 0);
      }
    };

    const { data } = supabase.auth.onAuthStateChange((event, newSession) => {
      apply(newSession);
      // On any successful sign-in, ensure the welcome email has been sent.
      // Server function is idempotent — it skips if welcome_email_sent_at is set.
      if (event === "SIGNED_IN" && newSession?.user) {
        // Defer: inside this callback the session may not be persisted yet, so
        // the bearer attacher would send an unauthenticated call (500).
        setTimeout(() => {
          void (async () => {
            const { data: s } = await supabase.auth.getSession();
            if (!s.session?.access_token) return;
            try {
              await sendWelcomeEmailFn({ data: {} });
            } catch (e) {
              console.warn("welcome email skipped", e);
            }
          })();
        }, 0);
      }

    });
    subscription = data.subscription;

    restoreTimeout = setTimeout(() => {
      if (cancelled || hasResolvedInitialSession) return;
      console.warn("[auth] session restore timed out; failing open to avoid admin loading loop");
      hasResolvedInitialSession = true;
      lastUserId = null;
      setSession(null);
      setLoading(false);
      setIsAdmin(false);
      setAdminChecked(true);
      writeCachedAdmin(false);
    }, AUTH_RESTORE_TIMEOUT_MS);

    const isValidJwt = (t: string | undefined | null) =>
      typeof t === "string" && t.split(".").length === 3 && t.length > 20;

    supabase.auth.getSession()
      .then(async ({ data }) => {
        let restoredSession = data.session ?? null;

        if (!restoredSession) {
          apply(null, { allowPreHydrationNull: true });
          return;
        }

        // If the stored access token is not a valid compact JWS (3 segments),
        // server-side getClaims() will throw "Invalid Compact JWS". Force a
        // refresh; if that still fails, sign the user out so they re-login
        // and obtain a clean JWT.
        if (!isValidJwt(restoredSession.access_token)) {
          try {
            const { data: refreshed, error: refreshError } = await supabase.auth.refreshSession();
            if (refreshError || !refreshed.session || !isValidJwt(refreshed.session.access_token)) {
              await supabase.auth.signOut();
              apply(null, { allowPreHydrationNull: true });
              return;
            }
            restoredSession = refreshed.session;
          } catch {
            await supabase.auth.signOut();
            apply(null, { allowPreHydrationNull: true });
            return;
          }
        }

        supabase.auth.getUser()
          .then(({ data: userData, error }) => {
            if (error || !userData.user) {
              void supabase.auth.signOut().finally(() => {
                apply(null, { allowPreHydrationNull: true });
              });
              return;
            }

            apply(restoredSession, { allowPreHydrationNull: true });
          })
          .catch(() => {
            void supabase.auth.signOut().finally(() => {
              apply(null, { allowPreHydrationNull: true });
            });
          });
      })

      .catch((error) => {
        console.error("[auth] failed to restore session", error);
        if (!cancelled) {
          hasResolvedInitialSession = true;
          lastUserId = null;
          setSession(null);
          setLoading(false);
          setIsAdmin(false);
          setAdminChecked(true);
          writeCachedAdmin(false);
        }
      });

    return () => {
      cancelled = true;
      if (restoreTimeout) clearTimeout(restoreTimeout);
      subscription?.unsubscribe();
    };
  }, [router, queryClient, sendWelcomeEmailFn]);


  useEffect(() => {
    let cancelled = false;
    let retryTimeout: ReturnType<typeof setTimeout> | null = null;

    // Wait for the initial auth session restore before deciding that admin
    // permissions are "checked". Otherwise a signed-in admin can be treated as
    // a confirmed non-admin for a moment and get bounced away from /admin.
    if (loading) {
      return;
    }

    if (!session?.user) {
      setIsAdmin(false);
      setAdminChecked(true);
      return;
    }
    // Revalidate silently in the background; don't flip adminChecked off so the
    // UI doesn't flash a loading screen for already-known admins.
    const checkAdminStatus = async (retriesLeft = ADMIN_STATUS_RETRIES) => {
      try {
        await supabase.auth.getUser();

        const [serverStatus, clientStatus] = await Promise.allSettled([
          getCurrentAdminStatusFn(),
          getClientAdminStatusFallback(),
        ]);

        const resolvedIsAdmin =
          (serverStatus.status === "fulfilled" && serverStatus.value.isAdmin) ||
          (clientStatus.status === "fulfilled" && clientStatus.value);

        if (serverStatus.status === "rejected" && clientStatus.status === "rejected") {
          throw serverStatus.reason ?? clientStatus.reason ?? new Error("admin status check failed");
        }

        if (!cancelled) {
          setIsAdmin(Boolean(resolvedIsAdmin));
          setAdminChecked(true);
          writeCachedAdmin(Boolean(resolvedIsAdmin));
        }
      } catch (error) {
        if (cancelled) return;
        if (retriesLeft > 0) {
          retryTimeout = setTimeout(() => {
            void checkAdminStatus(retriesLeft - 1);
          }, ADMIN_STATUS_RETRY_DELAY_MS);
          return;
        }
        console.error("[auth] admin status check failed", error);
        setIsAdmin(false);
        setAdminChecked(true);
        writeCachedAdmin(false);
      }
    };

    void checkAdminStatus();
    return () => {
      cancelled = true;
      if (retryTimeout) clearTimeout(retryTimeout);
    };
  }, [loading, session?.user?.id, adminCheckNonce, getCurrentAdminStatusFn]);


  const value = useMemo<AuthContextValue>(() => ({
    session,
    user: session?.user ?? null,
    isAdmin,
    loading,
    adminChecked,
    refreshAdminStatus: () => { setAdminCheckNonce((value) => value + 1); },
    signOut: async () => {
      writeCachedAdmin(false);
      queryClient.removeQueries({ queryKey: ["profile"] });
      await supabase.auth.signOut();
    },
  }), [session, isAdmin, loading, adminChecked, queryClient]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
